import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import InputAdornment from '@mui/material/InputAdornment';
import { useStudentApis } from '../../apis/StudentAPIs.js';

// Country codes with common options first
const COUNTRY_CODES = [
  { code: '+1', country: 'United States', flag: '🇺🇸' },
  { code: '+44', country: 'United Kingdom', flag: '🇬🇧' },
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
  { code: '+33', country: 'France', flag: '🇫🇷' },
  { code: '+49', country: 'Germany', flag: '🇩🇪' },
  { code: '+81', country: 'Japan', flag: '🇯🇵' },
  { code: '+52', country: 'Mexico', flag: '🇲🇽' },
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+86', country: 'China', flag: '🇨🇳' },
  { code: '+7', country: 'Russia', flag: '🇷🇺' },
  // Add more as needed
];

function AddStudent({ open, onClose, onSave }) {
    const {checkStudentIdExists} = useStudentApis();
    const [newStudent, setNewStudent] = useState({
        studentId: '',
        firstName: '',
        lastName: '',
        email: '',
        phoneAreaCode: '+1', // Default to USA
        phoneNumber: '',
        studentAddress: '',
        classes: '',
    });

    const [errors, setErrors] = useState({});
    const [isChecking, setIsChecking] = useState(false);

    const handleChange = (event) => {
        const { name, value } = event.target;
        let newValue = value;

        switch (name) {
            case 'studentId':
                newValue = value.replace(/\D/g, '');
                if (newValue.length > 20) newValue = newValue.substring(0, 20);
                break;
                
            case 'firstName':
            case 'lastName':
                newValue = value.replace(/[^a-zA-Z\s'-]/g, '');
                if (newValue.length > 50) newValue = newValue.substring(0, 50);
                break;
                
            case 'phoneNumber':
                newValue = value.replace(/[^\d\s()x-]/g, '');
                if (newValue.length > 20) newValue = newValue.substring(0, 20);
                break;
                
            case 'email':
                if (newValue.length > 255) newValue = newValue.substring(0, 255);
                break;
                
            case 'studentAddress':
            case 'classes':
                if (newValue.length > 255) newValue = newValue.substring(0, 255);
                break;
                
            default:
                break;
        }

        setNewStudent(prev => ({
            ...prev,
            [name]: newValue
        }));

        setErrors(prev => ({
            ...prev,
            [name]: ''
        }));
    };

    const validateFields = () => {
        const validationErrors = {};

        // Student ID validation
        if (!newStudent.studentId.trim()) {
            validationErrors.studentId = 'Student ID is required.';
        } else if (!/^\d+$/.test(newStudent.studentId)) {
            validationErrors.studentId = 'Student ID must contain only numbers.';
        }

        // Name validation
        if (!newStudent.firstName.trim()) {
            validationErrors.firstName = 'First name is required.';
        } else if (!/^[a-zA-Z\s'-]+$/.test(newStudent.firstName)) {
            validationErrors.firstName = 'Name can only contain letters, spaces, hyphens, and apostrophes.';
        }

        if (!newStudent.lastName.trim()) {
            validationErrors.lastName = 'Last name is required.';
        } else if (!/^[a-zA-Z\s'-]+$/.test(newStudent.lastName)) {
            validationErrors.lastName = 'Name can only contain letters, spaces, hyphens, and apostrophes.';
        }

        // Email validation
        if (!newStudent.email.trim()) {
            validationErrors.email = 'Email is required.';
        } else if (!/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(newStudent.email)) {
            validationErrors.email = 'Please enter a valid email address.';
        }

        // Phone validation
        const cleanedPhone = newStudent.phoneNumber.replace(/\D/g, '');
        if (!cleanedPhone) {
            validationErrors.phoneNumber = 'Phone number is required.';
        } else if (cleanedPhone.length < 7) {
            validationErrors.phoneNumber = 'Phone number must have at least 7 digits.';
        } else if (cleanedPhone.length > 15) {
            validationErrors.phoneNumber = 'Phone number cannot exceed 15 digits.';
        }

        return validationErrors;
    };

    const handleSave = async () => {
        const validationErrors = validateFields();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setIsChecking(true);
        try {
            // Check if student ID exists
            const exists = await checkStudentIdExists(newStudent.studentId);
            if (exists) {
                setErrors(prev => ({
                    ...prev,
                    studentId: 'This student ID already exists. Please use a different one.'
                }));
                return;
            }
            
            // Prepare student data
            const cleanedPhone = newStudent.phoneNumber.replace(/\D/g, '');
            const fullPhoneNumber = `${newStudent.phoneAreaCode}${cleanedPhone}`;
            
            const detroitDate = new Date().toLocaleString('en-US', { 
                timeZone: 'America/Detroit',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            }).replace(/(\d+)\/(\d+)\/(\d+)/, '$3-$1-$2');
            
            const success = await onSave({
                ...newStudent,
                phoneNumber: fullPhoneNumber,
                studentCreationDate: detroitDate
            });
            
            if (success) {
                onClose();
                // Reset form
                setNewStudent({
                    studentId: '',
                    firstName: '',
                    lastName: '',
                    email: '',
                    phoneAreaCode: '+1', // Reset to USA
                    phoneNumber: '',
                    studentAddress: '',
                    classes: '',
                });
            }
        } catch (error) {
            console.error('Error saving student:', error);
            setErrors(prev => ({
                ...prev,
                general: 'Failed to save student. Please try again later.'
            }));
        } finally {
            setIsChecking(false);
        }
    };

    // Format phone number for display
    const formatPhoneNumber = (input) => {
        if (!input) return '';
        
        const digits = input.replace(/\D/g, '');
        const areaCode = digits.substring(0, 3);
        const middle = digits.substring(3, 6);
        const end = digits.substring(6, 10);
        const extension = digits.substring(10, 15);
        
        let formatted = '';
        if (digits.length > 6) {
            formatted = `${areaCode} ${middle}-${end}`;
        } else if (digits.length > 3) {
            formatted = `${areaCode} ${middle}`;
        } else {
            formatted = digits;
        }
        
        if (extension) formatted += ` x${extension}`;
        return formatted;
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Add New Student</DialogTitle>
            <DialogContent sx={{ width: '800px' }}>
                {errors.general && (
                    <Typography color="error" paragraph>
                        {errors.general}
                    </Typography>
                )}
                
                <Stack spacing={2} mt={1}>
                    {Object.keys(newStudent).map((key) => {
                        if (key === 'phoneAreaCode') return null; 
                        
                        return (
                            <div key={key}>
                                {errors[key] && (
                                    <Typography variant="body2" color="error" gutterBottom>
                                        {errors[key]}
                                    </Typography>
                                )}
                                
                                {key === 'phoneNumber' ? (
                                    <TextField
                                        fullWidth
                                        label="Phone Number"
                                        name={key}
                                        value={formatPhoneNumber(newStudent[key])}
                                        onChange={handleChange}
                                        error={!!errors[key]}
                                        disabled={isChecking}
                                        inputProps={{
                                            maxLength: 20,
                                            inputMode: 'tel'
                                        }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <TextField
                                                        select
                                                        variant="standard"
                                                        value={newStudent.phoneAreaCode}
                                                        onChange={(e) => setNewStudent(prev => ({
                                                            ...prev,
                                                            phoneAreaCode: e.target.value
                                                        }))}
                                                        disabled={isChecking}
                                                        sx={{
                                                            width: '120px',
                                                            '& .MuiSelect-select': {
                                                                paddingRight: '8px',
                                                                fontSize: '0.875rem'
                                                            },
                                                            '& fieldset': { border: 'none' }
                                                        }}
                                                    >
                                                        {COUNTRY_CODES.map((option) => (
                                                            <MenuItem 
                                                                key={option.code} 
                                                                value={option.code}
                                                            >
                                                                {option.flag} {option.code}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                ) : (
                                    <TextField
                                        fullWidth
                                        label={key.replace(/([A-Z])/g, ' $1')
                                            .replace(/^./, str => str.toUpperCase())}
                                        name={key}
                                        value={newStudent[key]}
                                        onChange={handleChange}
                                        error={!!errors[key]}
                                        disabled={isChecking && key === 'studentId'}
                                        inputProps={{
                                            maxLength: key === 'studentId' ? 20 : 255,
                                            inputMode: key === 'studentId' ? 'numeric' : 'text'
                                        }}
                                    />
                                )}
                            </div>
                        );
                    })}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isChecking}>Cancel</Button>
                <Button 
                    onClick={handleSave} 
                    disabled={isChecking}
                    variant="contained"
                    color="primary"
                >
                    {isChecking ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default AddStudent;